import { transitions as purchaseTransitions } from '../transactions/transactionProcessPurchase';
import { toCanonicalTransitionName } from '../transactions/transitionAliases';
import { getStoreNameOrDisplayName } from './dataExtractors';
import {
  CANONICAL_STATUSES,
  getCanonicalStatus,
  getOfferDeadline as getOfferDeadlineFromStatusMap,
  getOfferTimeRemainingMs as getOfferTimeRemainingMsFromStatusMap,
} from './transactionStatusMap';

export const OFFER_RESPONSE_WINDOW_HOURS = 24;
export const OFFER_PAYMENT_WINDOW_HOURS = 24;
export const OFFER_RESPONSE_WINDOW_MS = OFFER_RESPONSE_WINDOW_HOURS * 60 * 60 * 1000;
export const OFFER_PAYMENT_WINDOW_MS = OFFER_PAYMENT_WINDOW_HOURS * 60 * 60 * 1000;

export const OFFER_TRANSITIONS = [
  purchaseTransitions.TRANSITION_SEND_OFFER,
  purchaseTransitions.TRANSITION_SEND_OFFER_AUTO_ELIGIBLE,
  purchaseTransitions.TRANSITION_ACCEPT_OFFER,
  purchaseTransitions.TRANSITION_ACCEPT_AUTO_ELIGIBLE_OFFER,
  purchaseTransitions.TRANSITION_DECLINE_OFFER,
  purchaseTransitions.TRANSITION_DECLINE_AUTO_ELIGIBLE_OFFER,
  purchaseTransitions.TRANSITION_COUNTER_OFFER,
  purchaseTransitions.TRANSITION_COUNTER_AUTO_ELIGIBLE_OFFER,
  purchaseTransitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER,
  purchaseTransitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER,
  purchaseTransitions.TRANSITION_AUTO_ACCEPT_OFFER,
  purchaseTransitions.TRANSITION_AUTO_EXPIRE_OFFER_PAYMENT,
];

export const OFFER_TRANSITIONS_WITHOUT_EXPIRY = [
  purchaseTransitions.TRANSITION_SEND_OFFER,
  purchaseTransitions.TRANSITION_SEND_OFFER_AUTO_ELIGIBLE,
  purchaseTransitions.TRANSITION_ACCEPT_OFFER,
  purchaseTransitions.TRANSITION_ACCEPT_AUTO_ELIGIBLE_OFFER,
  purchaseTransitions.TRANSITION_DECLINE_OFFER,
  purchaseTransitions.TRANSITION_DECLINE_AUTO_ELIGIBLE_OFFER,
  purchaseTransitions.TRANSITION_COUNTER_OFFER,
  purchaseTransitions.TRANSITION_COUNTER_AUTO_ELIGIBLE_OFFER,
  purchaseTransitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER,
  purchaseTransitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER,
  purchaseTransitions.TRANSITION_AUTO_ACCEPT_OFFER,
];

const OFFER_CONVERSATION_STATUSES = new Set([
  CANONICAL_STATUSES.OFFER_PENDING,
  CANONICAL_STATUSES.OFFER_COUNTERED,
  CANONICAL_STATUSES.OFFER_ACCEPTED,
  CANONICAL_STATUSES.OFFER_DECLINED,
  CANONICAL_STATUSES.OFFER_EXPIRED,
  CANONICAL_STATUSES.OFFER_PAYMENT_EXPIRED,
]);

export const OFFER_STATUS_BADGE_COLORS = {
  OFFER: '#f77f00',
  ACCEPTED: '#c6488c',
  DECLINED: '#59766c',
  EXPIRED: '#ED1B24',
  CANCELED: '#59766c',
  UNKNOWN: '#6f767e',
};

export const isOfferConversation = (tx, now = new Date()) => {
  const canonicalStatus = getCanonicalStatus(tx, null, { now });
  if (OFFER_CONVERSATION_STATUSES.has(canonicalStatus)) {
    return true;
  }

  const hasOfferParams = tx?.attributes?.protectedData?.offerParams?.isOffer === true;
  const lastTransition = toCanonicalTransitionName(tx?.attributes?.lastTransition);

  return hasOfferParams && OFFER_TRANSITIONS.includes(lastTransition);
};

export const filterOfferConversations = (transactions, now = new Date()) =>
  Array.isArray(transactions) ? transactions.filter(tx => isOfferConversation(tx, now)) : [];

export const filterMessageConversations = (transactions, now = new Date()) =>
  Array.isArray(transactions) ? transactions.filter(tx => !isOfferConversation(tx, now)) : [];

export const getOfferDeadline = (tx, now = new Date()) => getOfferDeadlineFromStatusMap(tx, now);

export const getOfferTimeRemainingMs = (tx, now = new Date()) =>
  getOfferTimeRemainingMsFromStatusMap(tx, now);

export const getOfferStatus = (tx, now = new Date()) => {
  const canonicalStatus = getCanonicalStatus(tx, null, { now });
  switch (canonicalStatus) {
    case CANONICAL_STATUSES.OFFER_PENDING:
      return 'pendingSeller';
    case CANONICAL_STATUSES.OFFER_COUNTERED:
      return 'countered';
    case CANONICAL_STATUSES.OFFER_ACCEPTED:
      return 'accepted';
    case CANONICAL_STATUSES.OFFER_DECLINED:
      return 'declined';
    case CANONICAL_STATUSES.OFFER_EXPIRED:
      return 'expired';
    case CANONICAL_STATUSES.OFFER_PAYMENT_EXPIRED:
      return 'paymentExpired';
    case CANONICAL_STATUSES.CANCELED:
      return 'canceled';
    default:
      return 'unknown';
  }
};

const getStatusMessage = status => {
  switch (status) {
    case 'canceled':
      return 'Canceled';
    case 'accepted':
      return 'Offer accepted!';
    case 'declined':
      return 'Offer declined.';
    case 'countered':
      return 'Counter offer made.';
    case 'acceptedAfterCounter':
      return 'Counter offer accepted!';
    case 'declinedAfterCounter':
      return 'Counter offer declined.';
    case 'expired':
    case 'paymentExpired':
      return 'Offer has expired';
    default:
      return '';
  }
};

const getOfferExpiryText = remainingMs => {
  if (typeof remainingMs !== 'number' || remainingMs <= 0) {
    return 'Offer has expired';
  }

  const totalHours = Math.ceil(remainingMs / (60 * 60 * 1000));
  if (totalHours > 24) {
    const totalDays = Math.ceil(totalHours / 24);
    const dayLabel = totalDays === 1 ? 'day' : 'days';
    return `Offer expires in ${totalDays} ${dayLabel}`;
  }

  const hourLabel = totalHours === 1 ? 'hour' : 'hours';
  return `Offer expires in ${totalHours} ${hourLabel}`;
};

export const getOfferStatusBadge = (tx, now = new Date()) => {
  const lastTransition = toCanonicalTransitionName(tx?.attributes?.lastTransition);
  const status = getOfferStatus(tx, now);
  const remainingTime = getOfferTimeRemainingMs(tx, now);
  const isOfferTransition = lastTransition === purchaseTransitions.TRANSITION_SEND_OFFER;
  const isCounterTransition = lastTransition === purchaseTransitions.TRANSITION_COUNTER_OFFER;
  const showExpiry = isOfferTransition || isCounterTransition;
  const isExpired = showExpiry && status === 'expired';

  if (status === 'canceled') {
    return {
      status,
      message: getStatusMessage(status),
      color: OFFER_STATUS_BADGE_COLORS.CANCELED,
      icon: null,
    };
  }

  if (isOfferTransition) {
    return {
      status,
      message: getOfferExpiryText(remainingTime),
      color: isExpired ? OFFER_STATUS_BADGE_COLORS.EXPIRED : OFFER_STATUS_BADGE_COLORS.OFFER,
      icon: isExpired ? 'expiredClock' : 'clock',
    };
  }

  if (isCounterTransition) {
    return {
      status,
      message: isExpired ? getStatusMessage('expired') : getStatusMessage('countered'),
      color: isExpired ? OFFER_STATUS_BADGE_COLORS.EXPIRED : OFFER_STATUS_BADGE_COLORS.OFFER,
      icon: isExpired ? 'expiredClock' : null,
    };
  }

  if (lastTransition === purchaseTransitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER) {
    return {
      status,
      message: getStatusMessage('acceptedAfterCounter'),
      color: OFFER_STATUS_BADGE_COLORS.ACCEPTED,
      icon: null,
    };
  }

  if (lastTransition === purchaseTransitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER) {
    return {
      status,
      message: getStatusMessage('declinedAfterCounter'),
      color: OFFER_STATUS_BADGE_COLORS.DECLINED,
      icon: null,
    };
  }

  if (status === 'accepted') {
    return {
      status,
      message: getStatusMessage('accepted'),
      color: OFFER_STATUS_BADGE_COLORS.ACCEPTED,
      icon: null,
    };
  }

  if (status === 'declined') {
    return {
      status,
      message: getStatusMessage('declined'),
      color: OFFER_STATUS_BADGE_COLORS.DECLINED,
      icon: null,
    };
  }

  if (status === 'paymentExpired') {
    return {
      status,
      message: getStatusMessage('paymentExpired'),
      color: OFFER_STATUS_BADGE_COLORS.EXPIRED,
      icon: 'expiredClock',
    };
  }

  return {
    status,
    message: getStatusMessage(status),
    color: OFFER_STATUS_BADGE_COLORS.UNKNOWN,
    icon: null,
  };
};

export const getOfferModalMessage = ({ tx, currentUser, now = new Date() }) => {
  if (!tx) {
    return {
      title: '',
      description: '',
      statusMessage: '',
      status: '',
      isExpired: false,
    };
  }

  const canonicalStatus = getCanonicalStatus(tx, null, { now });
  if (canonicalStatus === CANONICAL_STATUSES.CANCELED) {
    return {
      title: 'This offer has been canceled.',
      description: '',
      statusMessage: 'Canceled',
      status: 'canceled',
      isExpired: true,
    };
  }

  const transitionType = toCanonicalTransitionName(tx?.attributes?.lastTransition);
  const isCustomer = tx?.customer?.id?.uuid === currentUser?.id?.uuid;
  const offerPrice = tx?.attributes?.protectedData?.offerParams?.offerPrice;
  // Keep modal visibility aligned with offer actions: each stage has its own deadline,
  // and recorded terminal states must not be replaced by a creation-time calculation.
  const remainingMs = getOfferTimeRemainingMs(tx, now);
  const remainingHours = typeof remainingMs === 'number' ? remainingMs / (60 * 60 * 1000) : null;
  const isExpired =
    canonicalStatus === CANONICAL_STATUSES.OFFER_EXPIRED ||
    canonicalStatus === CANONICAL_STATUSES.OFFER_PAYMENT_EXPIRED;

  let state = { title: '', description: '', statusMessage: '', status: '' };

  switch (transitionType) {
    case purchaseTransitions.TRANSITION_SEND_OFFER: {
      let message = 'Offer has expired';
      if (!isExpired && typeof remainingHours === 'number') {
        const numHours = Math.ceil(remainingHours);
        const hourString = numHours === 1 ? 'hour' : 'hours';
        message = `Offer expires in ${numHours} ${hourString}`;
      }

      state = {
        statusMessage: message,
        status: 'offered',
        title: isCustomer
          ? isExpired
            ? `Your offer of $${offerPrice} has expired.`
            : `You’ve made an offer for $${offerPrice}. Your offer is valid for 24 hours. You will be notified with the seller response.`
          : `You have received an offer from ${getStoreNameOrDisplayName(
              tx?.customer
            )} for $${offerPrice}. What would you like to do?`,
      };
      break;
    }
    case purchaseTransitions.TRANSITION_AUTO_ACCEPT_OFFER:
    case purchaseTransitions.TRANSITION_ACCEPT_OFFER:
      state = {
        statusMessage: 'Offer accepted!',
        status: 'accepted',
        title:
          transitionType === purchaseTransitions.TRANSITION_AUTO_ACCEPT_OFFER
            ? `The offer of $${offerPrice} was accepted automatically.`
            : isCustomer
            ? `Congrats! Your offer has been accepted by ${getStoreNameOrDisplayName(
                tx?.provider
              )} for $${offerPrice}.`
            : `You have accepted ${getStoreNameOrDisplayName(
                tx?.customer
              )}'s offer for $${offerPrice}.`,
        description: isCustomer
          ? `Please click on the button below to complete the purchase at the accepted price.`
          : `The customer now needs to purchase the item. You can accept other offers in the meantime.`,
      };
      break;
    case purchaseTransitions.TRANSITION_DECLINE_OFFER:
      state = {
        statusMessage: 'Offer declined.',
        status: 'declined',
        title: isCustomer
          ? `Unfortunately your offer of $${offerPrice} has been declined by ${getStoreNameOrDisplayName(
              tx?.provider
            )} and no counter offer was provided. You can buy the item at the listed price or explore similar items.`
          : `You declined an offer of $${offerPrice} from ${getStoreNameOrDisplayName(
              tx?.customer
            )}`,
      };
      break;
    case purchaseTransitions.TRANSITION_COUNTER_OFFER:
      state = {
        statusMessage: 'Counter offer made.',
        status: 'pending',
        title: isCustomer
          ? `Good news! ${getStoreNameOrDisplayName(
              tx?.provider
            )} responded to your offer with a counter offer of $${offerPrice}.`
          : ` You gave an a counter offer of $${offerPrice} to ${getStoreNameOrDisplayName(
              tx?.customer
            )}.`,
        description: isCustomer
          ? `You have 24 hours to accept the offer or it will expire.`
          : `${getStoreNameOrDisplayName(
              tx?.customer
            )} has 24 hours to accept or reject your counter offer.`,
      };
      break;
    case purchaseTransitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER:
      state = {
        statusMessage: 'Counter offer accepted!',
        status: 'accepted',
        title: isCustomer
          ? `Congrats! You accepted counter offer by ${getStoreNameOrDisplayName(
              tx?.provider
            )} for $${offerPrice}.`
          : `Your counter offer has been accepted by ${getStoreNameOrDisplayName(
              tx?.customer
            )} for $${offerPrice}.`,
        description: isCustomer
          ? `You have 24 hours to complete the purchase. Please click on the button below to purchase the item for $${offerPrice}.`
          : `${getStoreNameOrDisplayName(
              tx?.customer
            )} has 24 hours to purchase before the offer expires.`,
      };
      break;
    case purchaseTransitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER:
      state = {
        statusMessage: 'Counter offer declined.',
        status: 'declined',
        title: isCustomer
          ? `You declined counter offer by ${getStoreNameOrDisplayName(
              tx?.provider
            )} for $${offerPrice}.`
          : `Your counter offer has been declined by ${getStoreNameOrDisplayName(
              tx?.customer
            )} for $${offerPrice}.`,
      };
      break;
    default:
      state = {
        statusMessage: '',
        status: '',
        title: '',
        description: '',
      };
  }

  if (isExpired) {
    return {
      ...state,
      title: `Your offer of $${offerPrice} has expired.`,
      description: '',
      statusMessage: 'Offer has expired',
      isExpired: true,
    };
  }

  return {
    ...state,
    isExpired: false,
  };
};

export const getOfferActions = (tx, currentUserId, now = new Date()) => {
  const status = getOfferStatus(tx, now);
  const providerId = tx?.provider?.id?.uuid;
  const customerId = tx?.customer?.id?.uuid;
  const isSeller = providerId && providerId === currentUserId;
  const isBuyer = customerId && customerId === currentUserId;

  return {
    canSellerAccept: isSeller && status === 'pendingSeller',
    canSellerDecline: isSeller && status === 'pendingSeller',
    canSellerCounter: isSeller && status === 'pendingSeller',
    canBuyerAcceptCounter: isBuyer && status === 'countered',
    canBuyerDeclineCounter: isBuyer && status === 'countered',
    canBuyerPay: isBuyer && status === 'accepted',
  };
};

export const formatOfferAmount = value => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return '0.00';
  }
  return amount.toFixed(2);
};
